import { ICellRendererParams } from '@ag-grid-enterprise/all-modules';
import { makeStyles, Typography } from '@material-ui/core';
import { useParams } from '@reach/router';
import React, { useState } from 'react';

import { api } from '../../../../core/api';
import { FontSizes } from '../../../../core/config/mainTheme';
import { getActiveDataset, getActiveDbVersion } from '../../../../core/utils/utils';
import { FileInfo } from '../../../../generated/model/models';
import { useAppGoogleReCaptcha } from '../../../../hooks/useAppGoogleReCaptcha';
import { useSnackbarMessage } from '../../../../hooks/useSnackbarMessage';
import { useBenchmarkState } from '../../../../pages/Benchmark/Context/BenchmarkContext';
import { useCompaniesState } from '../../../../pages/FinancialData/shared/CompanyContext/CompanyContext';
import { useErrorsDispatch } from '../../../../pages/FinancialData/shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../../pages/FinancialData/shared/ErrorContext/ErrorContext.types';

interface Props extends ICellRendererParams {
  data: FileInfo;
}

const FileDownloadCellRenderer: React.FC<Props> = ({ data }) => {
  const classes = useStyles();
  const [snackbarReportText, setSnackbarReportText] = useState<string>('');
  const { executeRecaptcha } = useAppGoogleReCaptcha();
  const dispatchErrors = useErrorsDispatch();
  const { company, dataset, dbVersion } = useCompaniesState();
  const companyUid = company?.companyInfo?.id;
  const { dataset: datasetParam, dbVersion: dbVersionParam } = useParams<{
    dataset?: string;
    dbVersion?: string;
  }>();

  const { activeBenchmark } = useBenchmarkState();
  const datasetSource = datasetParam ?? dataset;
  const dbVersionSource = dbVersionParam ?? dbVersion;
  const activeDataset = getActiveDataset(datasetSource, activeBenchmark);
  const activeDbVersion = getActiveDbVersion(dbVersionSource, activeBenchmark);

  const handleClick = async () => {
    setSnackbarReportText('companies:snackbarDownloadFilesText');
    try {
      if (data.internalId && companyUid) {
        const recaptchaToken = await executeRecaptcha?.('search');
        await api.company.getDocument(
          companyUid,
          recaptchaToken!,
          activeDataset,
          activeDbVersion,
          data.internalId,
          [],
        );
      }
    } catch (error) {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
    }
  };

  useSnackbarMessage(snackbarReportText !== '', snackbarReportText, {
    variant: 'info',
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'right',
    },
  });

  return (
    <Typography className={classes.link} onClick={handleClick}>
      {data.description}
    </Typography>
  );
};

const useStyles = makeStyles((theme) => ({
  link: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textDecoration: 'none',
    color: theme.palette.primary.main,
    fontWeight: FontSizes.fontWeight.bold,
    cursor: 'pointer',
    marginLeft: -1 * (theme.spacing(6) + 1),
    marginRight: -1 * (theme.spacing(6) + 1),
    paddingLeft: theme.spacing(6),
    lineHeight: 'inherit',
  },
}));

export default FileDownloadCellRenderer;
